<template>
    <div class="automation-template-sellsy">
        <div class="automation-template-sellsy__banner">
            <img src="/ressources/logged/img/illustrations/sellsy-automations.svg" />
        </div>
        <LayoutWithNavigation sidebarWidth="272px">
            <template slot="navigation">
                <CompassInput
                    v-model="searchTemplateValue"
                    prefixIcon="search"
                    :placeholder="$t.__('automation.AutomationTemplatesSellsy.placeholder_search')"
                    isEraseable
                    class="automation-template-sellsy__search"
                />
                <p
                    class="automation-template-sellsy__tags--title typographies-paragraph-small-semibold"
                >
                    {{ $t.__('common.labels.categories') }}
                </p>
                <div class="automation-template-sellsy__tags">
                    <CompassTagInteractive
                        v-for="(tag, index) in tagsTemplates"
                        :key="index"
                        :modelValue="tag.label"
                        variant="selectable"
                        color="gray"
                        :isSelected="tag.selected"
                        :valueLabel="tag.label"
                        @change="handleTagTemplates(index)"
                    />
                </div>
                <CompassPlainButton
                    v-if="isFiltered"
                    :label="$t.__('common.actions.reset')"
                    leftIcon="close"
                    @click="resetFilters"
                />
            </template>

            <template slot="default">
                <div
                    v-if="filteredTemplates.length > 0"
                    class="automation-template-sellsy__container"
                >
                    <AutomationTemplateCard
                        v-for="template in filteredTemplates"
                        :key="template.uuid"
                        :template="template"
                    >
                        <template slot="connector">
                            <template v-if="template.objects.type">
                                <CompassObject
                                    v-for="object in template.objects.type"
                                    :key="object.uuid"
                                    :object="object.type"
                                    size="small"
                                />
                            </template>
                        </template>
                    </AutomationTemplateCard>
                </div>
                <CompassEmptyState
                    v-else
                    illustrationPath="/ressources/logged/img/illustrations/no-results.svg"
                    :caption="translations.captionNoFilterResult"
                    class="automation-template-sellsy__empty-state"
                />
            </template>
        </LayoutWithNavigation>
    </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';

import LayoutWithNavigation from 'vcomponents/common/layout/LayoutWithNavigation.vue';
import AutomationTemplateCard from 'vcomponents/automation/templates/AutomationTemplateCard.vue';

import { getAutomationsTemplates } from 'api/automation/automationService.js';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateSystem } = createNamespacedHelpers('system');

export default {
    components: {
        LayoutWithNavigation,
        AutomationTemplateCard,
    },
    data() {
        return {
            translations: {
                featureBanner: {
                    title: this.$t.__('automation.AutomationTemplatesSellsy.banner_title'),
                    description: this.$t.__(
                        'automation.AutomationTemplatesSellsy.banner_description',
                    ),
                },
                captionNoFilterResult: this.$t.__('common.labels.caption_no_filter_result'),
            },
            tagsTemplates: [
                {
                    category: 'synchro_opp_doc',
                    label: this.$t.__('automation.AutomationTemplatesSellsy.tag_synchro_opp_doc'),
                    selected: false,
                },
                {
                    category: 'new_lead',
                    label: this.$t.__('automation.AutomationTemplatesSellsy.tag_new_lead'),
                    selected: false,
                },
                {
                    category: 'sales_process',
                    label: this.$t.__('automation.AutomationTemplatesSellsy.tag_sales_process'),
                    selected: false,
                },
                {
                    category: 'sales_doc',
                    label: this.$t.__('automation.AutomationTemplatesSellsy.tag_sales_doc'),
                    selected: false,
                },
                {
                    category: 'reminders',
                    label: this.$t.__('automation.AutomationTemplatesSellsy.tag_reminders'),
                    selected: false,
                },
            ],
            searchTemplateValue: '',
            templates: [],
        };
    },
    computed: {
        ...mapStateSystem(['errorsHandler']),

        isFiltered() {
            return this.searchTemplateValue || this.selectedCategories.length > 0;
        },
        filteredTemplates() {
            let filtered = this.templates;

            // Filter by search value
            if (this.searchTemplateValue) {
                filtered = filtered.filter((template) =>
                    template.title.toLowerCase().includes(this.searchTemplateValue.toLowerCase()),
                );
            }

            // Filter by selected categories
            const selectedCategories = this.selectedCategories;
            if (selectedCategories.length > 0) {
                filtered = filtered.filter((template) =>
                    selectedCategories.includes(template.category),
                );
            }

            return filtered;
        },
        selectedCategories() {
            return this.tagsTemplates.filter((tag) => tag.selected).map((tag) => tag.category);
        },
    },
    created() {
        this.getAutomationsTemplates();
    },
    methods: {
        getAutomationsTemplates() {
            getAutomationsTemplates()
                .then((response) => {
                    const templatesArray = response.data.data;
                    this.templates = templatesArray.map((template) => {
                        const tags = template.tags || [];
                        const category =
                            tags
                                .find((tag) => tag.startsWith('category:'))
                                ?.replace('category:', '') || '';
                        const icons =
                            tags
                                .filter((tag) => tag.startsWith('icon:'))
                                .map((icon) => icon.replace('icon:', '')) ?? [];

                        return {
                            uuid: uuidv4(),
                            objects: {
                                uuid: uuidv4(),
                                type: icons,
                            },
                            category,
                            id: template.id,
                            title: template.name,
                            description: template.description,
                            connectorTooltip: '',
                            isPopular: tags.includes('popular'),
                        };
                    });
                })
                .catch((error) => {
                    this.errorsHandler.logException(error);
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('common.labels.toast.error'),
                    });
                });
        },
        resetFilters() {
            this.searchTemplateValue = '';
            for (const tag of this.tagsTemplates) {
                tag.selected = false;
            }
        },
        handleTagTemplates(index) {
            this.tagsTemplates[index].selected = !this.tagsTemplates[index].selected;
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-template-sellsy {
    &__banner {
        border-radius: var(--spacing-0);
        margin: 0 0 var(--spacing-4);

        img {
            width: 100%;
        }
    }

    &__search {
        width: 100%;
        margin: 0 0 var(--spacing-5);
    }

    &__tags {
        display: flex;
        flex-wrap: wrap;
        gap: var(--spacing-1);
        margin: 0 0 var(--spacing-3);

        &--title {
            color: var(--colors-common-text-standard-caption);
            margin: 0 0 var(--spacing-1);
        }
    }

    &__container {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(228px, 1fr));
        gap: var(--spacing-3);
    }

    &__empty-state {
        margin-top: 56px;
    }
}
</style>
